/**

 * AGC: Admin Panel Component

 * AGC: Allows admin to view and respond to suggestions

 */



import React, { useState, useEffect, useRef, useMemo } from 'react'

import { X, Shield, MessageSquare, Send, LogOut, AlertCircle, Bot, Wallet, Server, Bug, Copy, Terminal, Trophy, Clock, RefreshCw, Lock, Gift, Menu, ChevronDown, ChevronRight } from 'lucide-react'

import { buildBackendUrl } from '../utils/backendUrl'

import { SuggestionDetail } from './SuggestionDetail'
import { AnticipatePayout } from './admin/AnticipatePayout'
import { StakingAdminControls } from './admin/StakingAdminControls'
import { AirdropAdminControls } from './admin/AirdropAdminControls'

import { ethers } from 'ethers'
import { useDebugSettings, DebugSettingKey } from '../hooks/useDebugSettings'
import { useModal } from '../hooks/useModal'



const ADMIN_BUFF_TYPES: Record<number, { label: string; emoji: string }> = {
  0: { label: 'Shield', emoji: '🛡️' },
  1: { label: 'Pierce', emoji: '🗡️' },
  2: { label: 'Haste', emoji: '⚡' },
  3: { label: 'Frost', emoji: '❄️' }
}



interface Comment {

  id: string

  text: string

  createdAt: number

  isAdmin?: boolean

}



interface Suggestion {

  id: string

  title: string

  category: string

  text: string

  createdAt: number

  updatedAt: number

  commentCount: number

  comments?: Comment[]

}



interface PlayerAISettings {

  enabled: boolean

  mode: 'instantly' | 'delay' | 'creator'

}



interface PlayerAITotals {

  totalWallets: number

  fundedWallets: number

  engagedWallets: number

}



interface PlayerAIQueueEntry {

  address: string

  level: number

  joinedAt: number

  isAI: boolean

}



interface PlayerAIStatus {

  settings: PlayerAISettings

  totals: PlayerAITotals

  queue: Record<string, PlayerAIQueueEntry[]>

}



interface PlayerAIWalletStatus {

  checkedAt: number

  ethBalance: string

  ogbBalance: string

  usdcBalance: string

  buffs: Array<{ id: number; balance: string }>

  meetsEthRequirement: boolean

  meetsOgbRequirement: boolean

  meetsUsdcRequirement: boolean

  isFunded: boolean

  playerData: {

    totalExperience: string

    experienceToNextLevel: string

    level: number

    totalBattles: number

    wins: number

    losses: number

    draws: number

  } | null

  ogbProfit?: string

  ogbBaseline?: string

}



interface PlayerAIWallet {

  index: number

  address: string

  configuredAddress?: string

  addressMismatch?: boolean

  isSharedWallet?: boolean // AGC: Flag if this wallet shares private key with another

  status: PlayerAIWalletStatus | null

  isEngaged: boolean

  usageCount: number

}



interface BackendLogEntry {

  id: number

  timestamp: number

  level: string

  message: string

  source?: string

  data?: Record<string, unknown>

  ip?: string

}



interface BackendStatusPayload {

  status: string

  timestamp: string

  uptime: string

  uptimeMs: number

  queueSize: number

  activePvPBattles: number

  activePvEBattles: number

  socketConnections: number

  firebaseConnected: boolean

  network: string

  chainId: string

  version: string

}



type AdminTabKey = 'playerai' | 'msgbox' | 'backendLogs' | 'tourney' | 'staking' | 'airdrop'
type PlayerAISubTab = 'controls' | 'wallets' | 'errors'
type StakingSubTab = 'anticipate' | 'controls'
type TourneySubTab = 'settings' | 'timers' | 'ongoing'

interface AdminNavSubItem {
  key: string
  label: string
  description?: string
  tab: AdminTabKey
  playerAISubTab?: PlayerAISubTab
  stakingSubTab?: StakingSubTab
  tourneySubTab?: TourneySubTab
}

interface AdminNavSection {
  key: string
  label: string
  description: string
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  tab: AdminTabKey
  children?: AdminNavSubItem[]
}



interface AdminPanelProps {

  onClose: () => void

}






interface AdminSuggestionDetailProps {
  suggestion: Suggestion
  authHeader: string
  onBack: () => void
  onClose: () => void
  onRefresh: () => void
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ onClose }) => {
  // AGC: Modal hook for Base App compatible alerts/confirms
  const { alert, confirm, Modals } = useModal()

  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const [username, setUsername] = useState('')

  const [password, setPassword] = useState('')

  const [loginError, setLoginError] = useState<string | null>(null)

  const [suggestions, setSuggestions] = useState<Suggestion[]>([])

  const [isLoading, setIsLoading] = useState(false)

  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null)

  const [authHeader, setAuthHeader] = useState<string | null>(null)

  const [playerAIStatus, setPlayerAIStatus] = useState<PlayerAIStatus | null>(null)

  const [isUpdatingPlayerAI, setIsUpdatingPlayerAI] = useState(false)

  const [activeTab, setActiveTab] = useState<AdminTabKey>('playerai')

  // AGC: Debug log when activeTab changes
  useEffect(() => {
    console.log('🔧 [AdminPanel] activeTab changed to:', activeTab)
  }, [activeTab])

  const [playerAISubTab, setPlayerAISubTab] = useState<PlayerAISubTab>('controls')
  
  // AGC: Staking tab subtabs
  const [stakingSubTab, setStakingSubTab] = useState<StakingSubTab>('controls')

  const [wallets, setWallets] = useState<PlayerAIWallet[]>([])

  const [isLoadingWallets, setIsLoadingWallets] = useState(false)

  const [errorLogs, setErrorLogs] = useState<Array<{

    id: string

    timestamp: number

    type: string

    message: string

    context: any

  }>>([])

  const [backendLogs, setBackendLogs] = useState<BackendLogEntry[]>([])

  const [isLoadingBackendLogs, setIsLoadingBackendLogs] = useState(false)

  const [backendLogsError, setBackendLogsError] = useState<string | null>(null)

  const [isLoadingErrors, setIsLoadingErrors] = useState(false)

  const backendLogsLoadedRef = useRef(false)

  const [backendStatus, setBackendStatus] = useState<BackendStatusPayload | null>(null)

  const [backendStatusError, setBackendStatusError] = useState<string | null>(null)
  
  const [isRestarting, setIsRestarting] = useState(false)

  // AGC: TournamentAI state
  const [tournamentAIStatus, setTournamentAIStatus] = useState<{
    settings: {
      enabled: boolean
      autoJoin: boolean
      targetPlayers: number
      maxPerTournament: number
      allowedWallets?: string[]
    }
    totals: {
      totalWallets: number
      fundedWallets: number
      engagedWallets: number
    }
    wallets: Array<{
      index: number
      address: string
      configuredAddress?: string
      status: any
      isEngaged: boolean
      usageCount: number
    }>
  } | null>(null)

  const [isUpdatingTournamentAI, setIsUpdatingTournamentAI] = useState(false)
  const [ongoingTournaments, setOngoingTournaments] = useState<Array<{
    id: string
    status: string
    createdAt: number
    startedAt?: number
    currentRound: number
    participantCount: number
    participants: Array<{ playerId: string; isActive: boolean }>
  }>>([])
  const [isLoadingOngoingTournaments, setIsLoadingOngoingTournaments] = useState(false)
  const [isCancellingTournament, setIsCancellingTournament] = useState<string | null>(null)
  
  // AGC: Tournament timer settings state
  const [tourneySubTab, setTourneySubTab] = useState<TourneySubTab>('settings')
  const [tournamentTimers, setTournamentTimers] = useState<{
    lobbyTimerMs: number
    readyTimerMs: number
    playMatchTimerMs: number
    autoCreateTournaments?: boolean
  } | null>(null)
  const [isLoadingTimers, setIsLoadingTimers] = useState(false)
  const [isUpdatingTimers, setIsUpdatingTimers] = useState(false)
  const [isStartingTournament, setIsStartingTournament] = useState(false)

  const [isNavOpen, setIsNavOpen] = useState(false)
  const [expandedSection, setExpandedSection] = useState<string>('playerai')

  const {
    settings: debugSettings,
    updateSettings: updateDebugSettings,
    isLoading: isDebugSettingsLoading,
    lastUpdatedAt: debugSettingsLastUpdatedAt,
  } = useDebugSettings()

  type DebugToggleOption = {

    key: DebugSettingKey

    label: string

    description: string

    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>

  }

  const debugToggleOptions: DebugToggleOption[] = [

    {

      key: 'debugButtonEnabled',

      label: 'Debug Button',

      description: 'Floating QA helper with runtime stats, sockets, and manual refresh actions.',

      icon: Bug

    },

    {

      key: 'erudaButtonEnabled',

      label: 'Eruda Console',

      description: 'Shows the Eruda in-frame console launcher for on-device debugging.',

      icon: Terminal

    },

    {

      key: 'copyButtonEnabled',

      label: 'Copy Console Logs',

      description: 'Displays the clipboard helpers for console + error logs (with device info).',

      icon: Copy

    }

  ]

  const navSections = useMemo<AdminNavSection[]>(() => [
    {
      key: 'playerai',
      label: 'Player AI',
      description: 'Automation, queues, and wallet health',
      icon: Bot,
      tab: 'playerai',
      children: [
        {
          key: 'playerai-controls',
          label: 'Controls',
          description: 'Automation switches + thresholds',
          tab: 'playerai',
          playerAISubTab: 'controls'
        },
        {
          key: 'playerai-wallets',
          label: 'Wallet Health',
          description: 'Balances, buffs, and XP levels',
          tab: 'playerai',
          playerAISubTab: 'wallets'
        },
        {
          key: 'playerai-errors',
          label: 'Error Queue',
          description: 'Failed wallet checks + context',
          tab: 'playerai',
          playerAISubTab: 'errors'
        }
      ]
    },
    {
      key: 'msgbox',
      label: 'Message Box',
      description: 'Community feedback + replies',
      icon: MessageSquare,
      tab: 'msgbox'
    },
    {
      key: 'backendLogs',
      label: 'Backend Logs',
      description: 'Realtime server output + filters',
      icon: Server,
      tab: 'backendLogs'
    },
    {
      key: 'tourney',
      label: 'Tournaments',
      description: 'Lobby automation + live rounds',
      icon: Trophy,
      tab: 'tourney',
      children: [
        {
          key: 'tourney-settings',
          label: 'AI Settings',
          description: 'Auto-join rules + targets',
          tab: 'tourney',
          tourneySubTab: 'settings'
        },
        {
          key: 'tourney-timers',
          label: 'Timers',
          description: 'Lobby and ready timers',
          tab: 'tourney',
          tourneySubTab: 'timers'
        },
        {
          key: 'tourney-ongoing',
          label: 'Ongoing Events',
          description: 'Active brackets + status',
          tab: 'tourney',
          tourneySubTab: 'ongoing'
        }
      ]
    },
    {
      key: 'staking',
      label: 'Staking',
      description: 'Revenue split and payouts',
      icon: Lock,
      tab: 'staking',
      children: [
        {
          key: 'staking-anticipate',
          label: 'Anticipate Payout',
          description: 'Queue + staging payouts',
          tab: 'staking',
          stakingSubTab: 'anticipate'
        },
        {
          key: 'staking-controls',
          label: 'Controls',
          description: 'Staking manager actions',
          tab: 'staking',
          stakingSubTab: 'controls'
        }
      ]
    },
    {
      key: 'airdrop',
      label: 'Airdrop',
      description: 'Rounds, funding, and stats',
      icon: Gift,
      tab: 'airdrop'
    }
  ], [])

  const playerAISubTabLabels: Record<PlayerAISubTab, string> = {
    controls: 'Controls',
    wallets: 'Wallet Health',
    errors: 'Error Queue'
  }

  const stakingSubTabLabels: Record<StakingSubTab, string> = {
    anticipate: 'Anticipate Payout',
    controls: 'Admin Controls'
  }

  const tourneySubTabLabels: Record<TourneySubTab, string> = {
    settings: 'AI Settings',
    timers: 'Timers',
    ongoing: 'Ongoing Events'
  }

  const collapseNavIfMobile = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setIsNavOpen(false)
    }
  }

  const handleNavSelection = (
    tab: AdminTabKey,
    options?: {
      playerAISubTab?: PlayerAISubTab
      stakingSubTab?: StakingSubTab
      tourneySubTab?: TourneySubTab
    }
  ) => {
    setActiveTab(tab)
    if (options?.playerAISubTab) {
      setPlayerAISubTab(options.playerAISubTab)
    }
    if (options?.stakingSubTab) {
      setStakingSubTab(options.stakingSubTab)
    }
    if (options?.tourneySubTab) {
      setTourneySubTab(options.tourneySubTab)
    }
    collapseNavIfMobile()
  }

  useEffect(() => {
    const sectionsWithChildren: AdminTabKey[] = ['playerai', 'staking', 'tourney']
    if (sectionsWithChildren.includes(activeTab)) {
      setExpandedSection(activeTab)
    }
  }, [activeTab])

  const handleSectionToggle = (section: AdminNavSection) => {
    if (section.children && section.children.length > 0) {
      setExpandedSection((prev) => (prev === section.key ? '' : section.key))
      return
    }
    handleNavSelection(section.tab)
  }

  const handleDebugSettingToggle = (key: DebugSettingKey) => {
    console.log('🎯 AGC: Admin panel toggle clicked:', key, 'isLoading:', isDebugSettingsLoading)
    if (isDebugSettingsLoading) {
      console.log('⚠️ AGC: Ignoring toggle - settings are loading')
      return
    }

    console.log('🔄 AGC: Toggling setting:', key, 'current value:', debugSettings[key])
    updateDebugSettings({

      [key]: !debugSettings[key]

    })

  }



  // AGC: Check if already authenticated (from localStorage)

  useEffect(() => {

    const storedAuth = localStorage.getItem('admin_auth')

    if (storedAuth) {

      setAuthHeader(storedAuth)

      setIsAuthenticated(true)

      fetchSuggestions(storedAuth)

      fetchPlayerAIStatus(storedAuth)

      fetchBackendStatus(storedAuth)

      fetchTournamentAIStatus(storedAuth)

    }

  }, [])



  // AGC: Fetch wallets when wallets subtab is active or tourney tab is active
  useEffect(() => {

    if (authHeader && ((activeTab === 'playerai' && playerAISubTab === 'wallets') || activeTab === 'tourney')) {

      fetchWallets(authHeader)

    }

  }, [authHeader, activeTab, playerAISubTab])



  // AGC: Fetch error logs when errors subtab is active

  useEffect(() => {

    if (authHeader && activeTab === 'playerai' && playerAISubTab === 'errors') {

      fetchErrorLogs(authHeader)

    }

  }, [authHeader, activeTab, playerAISubTab])

  // AGC: Fetch TournamentAI status when tourney tab is active
  useEffect(() => {
    if (authHeader && activeTab === 'tourney') {
      fetchTournamentAIStatus(authHeader)
      fetchOngoingTournaments(authHeader)
      fetchTournamentTimers(authHeader)
    }
  }, [authHeader, activeTab])
  
  // AGC: Fetch ongoing tournaments
  const fetchOngoingTournaments = async (auth: string) => {
    setIsLoadingOngoingTournaments(true)
    try {
      const response = await fetch(buildBackendUrl('admin/tournaments/ongoing'), {
        headers: {
          'Authorization': auth
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch ongoing tournaments')
      }
      
      const data = await response.json()
      setOngoingTournaments(data.tournaments || [])
    } catch (error) {
      console.error('Failed to load ongoing tournaments:', error)
      setOngoingTournaments([])
    } finally {
      setIsLoadingOngoingTournaments(false)
    }
  }
  
  // AGC: Cancel tournament
  const handleCancelTournament = async (tournamentId: string) => {
    if (!authHeader) return
    
    const confirmed = await confirm('Are you sure you want to cancel this tournament? All players will be refunded their entry fees.', {
      title: 'Cancel Tournament',
      confirmVariant: 'danger'
    })
    if (!confirmed) {
      return
    }
    
    setIsCancellingTournament(tournamentId)
    try {
      const response = await fetch(buildBackendUrl(`admin/tournaments/${tournamentId}/cancel`), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to cancel tournament' }))
        throw new Error(errorData.error || 'Failed to cancel tournament')
      }
      
      const data = await response.json()
      await alert(`Tournament cancelled successfully. ${data.refundedCount} players refunded.`, { type: 'success' })
      
      // AGC: Refresh ongoing tournaments list
      await fetchOngoingTournaments(authHeader)
    } catch (error: any) {
      console.error('Failed to cancel tournament:', error)
      await alert(`Failed to cancel tournament: ${error.message || 'Unknown error'}`, { type: 'error' })
    } finally {
      setIsCancellingTournament(null)
    }
  }



  // AGC: Load backend logs when the backend logs tab is opened

  useEffect(() => {

    if (authHeader && activeTab === 'backendLogs') {

      const silent = backendLogsLoadedRef.current

      fetchBackendLogs(authHeader, { silent })

      backendLogsLoadedRef.current = true

    }

  }, [authHeader, activeTab])



  const handleLogin = async (e: React.FormEvent) => {

    e.preventDefault()

    setLoginError(null)



    try {

      // AGC: Create Basic Auth header

      const credentials = btoa(`${username}:${password}`)

      const authHeaderValue = `Basic ${credentials}`



      // AGC: Test authentication by calling admin status endpoint

      const response = await fetch(buildBackendUrl('admin/status?format=json'), {

        headers: {

          'Authorization': authHeaderValue,

          'Accept': 'application/json'

        }

      })



      if (!response.ok) {

        throw new Error('Invalid credentials')

      }



      const statusData: BackendStatusPayload = await response.json()

      setBackendStatus(statusData)

      setBackendStatusError(null)



      // AGC: Store auth in localStorage

      localStorage.setItem('admin_auth', authHeaderValue)

      setAuthHeader(authHeaderValue)

      setIsAuthenticated(true)

      fetchSuggestions(authHeaderValue)

      fetchPlayerAIStatus(authHeaderValue)

      fetchBackendStatus(authHeaderValue)

      fetchTournamentAIStatus(authHeaderValue)

    } catch (err: any) {

      setLoginError(err.message || 'Login failed. Please check your credentials.')

    }

  }



  const handleLogout = () => {

    localStorage.removeItem('admin_auth')

    setAuthHeader(null)

    setIsAuthenticated(false)

    setSuggestions([])

    setSelectedSuggestion(null)

    setUsername('')

    setPassword('')

    setPlayerAIStatus(null)

    setBackendStatus(null)

    setBackendStatusError(null)

  }



  const fetchSuggestions = async (auth: string) => {

    try {

      setIsLoading(true)

      const response = await fetch(buildBackendUrl('api/suggestions'), {

        headers: {

          'Authorization': auth

        }

      })



      if (!response.ok) {

        throw new Error('Failed to fetch suggestions')

      }



      const data = await response.json()

      setSuggestions(data.suggestions || [])

    } catch (err: any) {

      console.error('Failed to load suggestions:', err)

    } finally {

      setIsLoading(false)

    }

  }



  const fetchPlayerAIStatus = async (auth: string) => {

    try {

      const response = await fetch(buildBackendUrl('admin/playerai/status'), {

        headers: {

          'Authorization': auth

        }

      })



      if (!response.ok) {

        throw new Error('Failed to fetch PlayerAI status')

      }



      const data = await response.json()

      setPlayerAIStatus(data)

    } catch (error) {

      console.error('Failed to load PlayerAI status:', error)

    }

  }



  const fetchErrorLogs = async (auth: string) => {

    setIsLoadingErrors(true)

    try {

      const response = await fetch(buildBackendUrl('admin/playerai/errors'), {

        headers: {

          'Authorization': auth

        }

      })



      if (!response.ok) {

        throw new Error('Failed to fetch error logs')

      }



      const data = await response.json()

      setErrorLogs(data.errors || [])

    } catch (error) {

      console.error('Failed to fetch error logs:', error)

      setErrorLogs([])

    } finally {

      setIsLoadingErrors(false)

    }

  }



  const fetchBackendLogs = async (auth: string, { silent = false }: { silent?: boolean } = {}) => {

    if (!silent) {

      setIsLoadingBackendLogs(true)

    }

    setBackendLogsError(null)



    try {

      const response = await fetch(buildBackendUrl('admin/debug/logs?limit=200'), {

        headers: {

          Authorization: auth

        }

      })



      if (!response.ok) {

        throw new Error('Failed to fetch backend logs')

      }



      const data = await response.json()

      setBackendLogs(Array.isArray(data.logs) ? data.logs : [])

    } catch (error: any) {

      console.error('Failed to load backend logs:', error)

      setBackendLogsError(error?.message || 'Unable to load backend logs')

      setBackendLogs([])

    } finally {

      if (!silent) {

        setIsLoadingBackendLogs(false)

      }

    }

  }



  const fetchBackendStatus = async (auth: string) => {

    setBackendStatusError(null)



    try {

      const response = await fetch(buildBackendUrl('admin/status?format=json'), {

        headers: {

          Authorization: auth,

          Accept: 'application/json'

        }

      })



      if (!response.ok) {

        throw new Error('Failed to fetch backend status')

      }



      const data: BackendStatusPayload = await response.json()

      setBackendStatus(data)

    } catch (error: any) {

      console.error('Failed to load backend status:', error)

      setBackendStatusError(error?.message || 'Unable to load backend status')

    }

  }



  const handleClearErrors = async () => {

    if (!authHeader) return



    const confirmed = await confirm('Are you sure you want to clear all error logs?', {
      title: 'Clear Error Logs',
      confirmVariant: 'danger'
    })
    if (!confirmed) {
      return
    }



    try {

      const response = await fetch(buildBackendUrl('admin/playerai/errors/clear'), {

        method: 'POST',

        headers: {

          'Authorization': authHeader

        }

      })



      if (!response.ok) {

        throw new Error('Failed to clear error logs')

      }



      setErrorLogs([])

    } catch (error) {

      console.error('Failed to clear error logs:', error)

      await alert('Failed to clear error logs', { type: 'error' })

    }

  }



  const handleRefreshBackendLogs = async () => {

    if (!authHeader) {

      return

    }

    fetchBackendLogs(authHeader)

    fetchBackendStatus(authHeader)

  }

  // AGC: Fetch TournamentAI status
  const fetchTournamentAIStatus = async (auth: string) => {
    try {
      const response = await fetch(buildBackendUrl('admin/tournamentai/status'), {
        headers: {
          'Authorization': auth
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch TournamentAI status')
      }

      const data = await response.json()
      setTournamentAIStatus(data)
    } catch (error) {
      console.error('Failed to load TournamentAI status:', error)
    }
  }

  // AGC: Fetch tournament timer settings
  const fetchTournamentTimers = async (auth: string) => {
    setIsLoadingTimers(true)
    try {
      const response = await fetch(buildBackendUrl('admin/tournament/timers'), {
        headers: {
          'Authorization': auth
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch tournament timer settings')
      }

      const data = await response.json()
      setTournamentTimers(data.settings)
    } catch (error) {
      console.error('Failed to load tournament timer settings:', error)
    } finally {
      setIsLoadingTimers(false)
    }
  }

  // AGC: Update tournament timer setting
  const handleUpdateTimer = async (timerName: 'lobbyTimerMs' | 'readyTimerMs' | 'playMatchTimerMs', valueMs: number) => {
    if (!authHeader) return

    setIsUpdatingTimers(true)
    try {
      const response = await fetch(buildBackendUrl('admin/tournament/timers'), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [timerName]: valueMs })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update timer' }))
        throw new Error(errorData.error || 'Failed to update timer')
      }

      const data = await response.json()
      setTournamentTimers(data.settings)
    } catch (error: any) {
      console.error('Failed to update timer:', error)
      await alert(`Failed to update timer: ${error.message || 'Unknown error'}`, { type: 'error' })
    } finally {
      setIsUpdatingTimers(false)
    }
  }

  // AGC: Reset tournament timers to defaults
  const handleResetTimers = async () => {
    if (!authHeader) return

    const confirmed = await confirm('Are you sure you want to reset all timers to default values (2 minutes)?', {
      title: 'Reset Timers',
      confirmVariant: 'danger'
    })
    if (!confirmed) {
      return
    }

    setIsUpdatingTimers(true)
    try {
      const response = await fetch(buildBackendUrl('admin/tournament/timers/reset'), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to reset timers')
      }

      const data = await response.json()
      setTournamentTimers(data.settings)
    } catch (error: any) {
      console.error('Failed to reset timers:', error)
      await alert(`Failed to reset timers: ${error.message || 'Unknown error'}`, { type: 'error' })
    } finally {
      setIsUpdatingTimers(false)
    }
  }

  // AGC: Toggle auto-create tournaments setting
  const handleToggleAutoCreateTournaments = async () => {
    if (!authHeader || tournamentTimers === null) return

    setIsUpdatingTimers(true)
    try {
      // AGC: Calculate new value - if currently ON (true or undefined), turn OFF (false)
      const newValue = !(tournamentTimers.autoCreateTournaments !== false)
      console.log('🔄 Toggling auto-create tournaments:', { current: tournamentTimers.autoCreateTournaments, new: newValue })
      
      const response = await fetch(buildBackendUrl('admin/tournament/autocreate'), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: newValue })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update auto-create setting' }))
        throw new Error(errorData.error || 'Failed to update auto-create setting')
      }

      const data = await response.json()
      console.log('✅ Auto-create tournaments updated:', data.settings)
      setTournamentTimers(data.settings)
    } catch (error: any) {
      console.error('Failed to toggle auto-create tournaments:', error)
      await alert(`Failed to toggle auto-create tournaments: ${error.message || 'Unknown error'}`, { type: 'error' })
    } finally {
      setIsUpdatingTimers(false)
    }
  }

  // AGC: Manually start a tournament
  const handleStartTournament = async () => {
    if (!authHeader) return

    setIsStartingTournament(true)
    try {
      const response = await fetch(buildBackendUrl('admin/tournament/start'), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to start tournament' }))
        throw new Error(errorData.error || 'Failed to start tournament')
      }

      const data = await response.json()
      console.log('✅ Tournament created:', data)
      const tournamentId = data.tournament?.id || data.tournamentId
      await alert(`Tournament created! ID: ${tournamentId?.slice(0, 10)}...`, { type: 'success', title: 'Tournament Created!' })
      
      // AGC: Refresh ongoing tournaments list
      fetchOngoingTournaments(authHeader)
    } catch (error: any) {
      console.error('Failed to start tournament:', error)
      await alert(`Failed to start tournament: ${error.message || 'Unknown error'}`, { type: 'error' })
    } finally {
      setIsStartingTournament(false)
    }
  }

  // AGC: Format milliseconds to human-readable time
  const formatTimerMs = (ms: number) => {
    if (ms >= 60000) {
      const minutes = Math.floor(ms / 60000)
      const seconds = Math.floor((ms % 60000) / 1000)
      if (seconds === 0) {
        return `${minutes} min`
      }
      return `${minutes}m ${seconds}s`
    }
    const seconds = Math.floor(ms / 1000)
    return `${seconds}s`
  }

  // AGC: Toggle TournamentAI enabled (now also controls auto-join)
  const handleToggleTournamentAIEnabled = async () => {
    if (!authHeader || !tournamentAIStatus) return

    setIsUpdatingTournamentAI(true)
    try {
      const newEnabled = !tournamentAIStatus.settings.enabled
      const response = await fetch(buildBackendUrl('admin/tournamentai/enabled'), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: newEnabled })
      })

      if (!response.ok) {
        throw new Error('Failed to update TournamentAI enabled status')
      }

      const data = await response.json()
      setTournamentAIStatus(prev => prev ? {
        ...prev,
        settings: { ...prev.settings, ...data.settings }
      } : null)
    } catch (error) {
      console.error('Failed to toggle TournamentAI enabled:', error)
      await alert('Failed to update TournamentAI enabled status', { type: 'error' })
    } finally {
      setIsUpdatingTournamentAI(false)
    }
  }

  // AGC: Update TournamentAI max per tournament
  const handleUpdateMaxPerTournament = async (maxPerTournament: number) => {
    if (!authHeader || !tournamentAIStatus) return

    if (maxPerTournament < 1 || maxPerTournament > 5) {
      await alert('Max per tournament must be between 1 and 5', { type: 'warning' })
      return
    }

    setIsUpdatingTournamentAI(true)
    try {
      const response = await fetch(buildBackendUrl('admin/tournamentai/maxpertournament'), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ maxPerTournament })
      })

      if (!response.ok) {
        throw new Error('Failed to update max per tournament')
      }

      const data = await response.json()
      setTournamentAIStatus(prev => prev ? {
        ...prev,
        settings: { ...prev.settings, ...data.settings }
      } : null)
    } catch (error) {
      console.error('Failed to update max per tournament:', error)
      await alert('Failed to update max per tournament', { type: 'error' })
    } finally {
      setIsUpdatingTournamentAI(false)
    }
  }

  // AGC: Update TournamentAI allowed wallets
  const handleUpdateAllowedWallets = async (allowedWallets: string[]) => {
    if (!authHeader || !tournamentAIStatus) return

    setIsUpdatingTournamentAI(true)
    try {
      const response = await fetch(buildBackendUrl('admin/tournamentai/allowedwallets'), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ allowedWallets })
      })

      if (!response.ok) {
        throw new Error('Failed to update allowed wallets')
      }

      const data = await response.json()
      setTournamentAIStatus(prev => prev ? {
        ...prev,
        settings: { ...prev.settings, ...data.settings }
      } : null)
    } catch (error) {
      console.error('Failed to update allowed wallets:', error)
      await alert('Failed to update allowed wallets', { type: 'error' })
    } finally {
      setIsUpdatingTournamentAI(false)
    }
  }

  // AGC: Handle backend restart
  const handleRestartBackend = async () => {
    if (!authHeader) {
      return
    }

    const confirmed = await confirm('Are you sure you want to restart the backend server?\n\nThis will temporarily disconnect all users and may take a few seconds to restart.', {
      title: 'Restart Backend Server',
      confirmVariant: 'danger'
    })
    if (!confirmed) {
      return
    }

    try {
      setIsRestarting(true)
      console.log('🔄 AGC: Requesting backend restart...')

      const response = await fetch(buildBackendUrl('admin/restart'), {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to restart backend`)
      }

      const data = await response.json()
      console.log('✅ AGC: Restart initiated:', data)

      // AGC: Show success message
      await alert('Backend restart initiated!\n\nThe server will restart in a few seconds. Please wait and then refresh this page.', { type: 'success', title: 'Restart Initiated' })

      // AGC: Wait a bit, then try to reconnect
      setTimeout(() => {
        console.log('🔄 AGC: Attempting to reconnect after restart...')
        fetchBackendStatus(authHeader)
        fetchWallets(authHeader)
        fetchPlayerAIStatus(authHeader)
      }, 5000)

    } catch (error: any) {
      console.error('❌ AGC: Failed to restart backend:', error)
      await alert(`Failed to restart backend: ${error.message}\n\nPlease restart manually if needed.`, { type: 'error' })
    } finally {
      setIsRestarting(false)
    }
  }



  // AGC: Fetch wallets with retry logic and timeout
  const fetchWallets = async (auth: string, retries = 3) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        setIsLoadingWallets(true)

        // AGC: Create abort controller for timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

        try {
          const response = await fetch(buildBackendUrl('admin/playerai/wallets'), {
            headers: {
              'Authorization': auth
            },
            signal: controller.signal
          })

          clearTimeout(timeoutId)

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to fetch wallets`)
          }

          const data = await response.json()
          setWallets(data.wallets || [])
          
          // AGC: Success - exit retry loop
          return
        } catch (fetchError: any) {
          clearTimeout(timeoutId)
          throw fetchError
        }
      } catch (error: any) {
        const isLastAttempt = attempt === retries
        const isTimeout = error.name === 'AbortError' || error.message?.includes('timeout')
        
        console.error(`❌ AGC: Failed to load wallets (attempt ${attempt}/${retries}):`, error)
        
        if (isLastAttempt) {
          console.error('❌ AGC: All wallet fetch attempts failed')
          // AGC: Show error to user on final attempt
          const errorMsg = isTimeout 
            ? 'Request timed out - backend may be unresponsive'
            : error.message || 'Unknown error'
          await alert(`Failed to load wallets after ${retries} attempts:\n${errorMsg}\n\nTry clicking "Restart Backend" if the issue persists.`, { type: 'error' })
        } else {
          // AGC: Wait before retry (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000)
          console.log(`⏳ AGC: Retrying wallet fetch in ${delay}ms...`)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      } finally {
        if (attempt === retries) {
          setIsLoadingWallets(false)
        }
      }
    }
  }



  const handleTogglePlayerAI = async () => {

    if (!authHeader || !playerAIStatus || isUpdatingPlayerAI) {

      return

    }



    try {

      setIsUpdatingPlayerAI(true)

      const response = await fetch(buildBackendUrl('admin/playerai/toggle'), {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

          'Authorization': authHeader

        },

        body: JSON.stringify({ enabled: !playerAIStatus.settings.enabled })

      })



      if (!response.ok) {

        throw new Error('Failed to update PlayerAI status')

      }



      await fetchPlayerAIStatus(authHeader)

    } catch (error: any) {

      console.error('Failed to toggle PlayerAI:', error)

      await alert(error.message || 'Failed to toggle PlayerAI', { type: 'error' })

    } finally {

      setIsUpdatingPlayerAI(false)

    }

  }



  const handleModeChange = async (mode: PlayerAISettings['mode']) => {

    if (!authHeader || !playerAIStatus || playerAIStatus.settings.mode === mode || isUpdatingPlayerAI) {

      return

    }



    try {

      setIsUpdatingPlayerAI(true)

      const response = await fetch(buildBackendUrl('admin/playerai/mode'), {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

          'Authorization': authHeader

        },

        body: JSON.stringify({ mode })

      })



      if (!response.ok) {

        throw new Error('Failed to update PlayerAI mode')

      }



      await fetchPlayerAIStatus(authHeader)

    } catch (error: any) {

      console.error('Failed to change PlayerAI mode:', error)

      await alert(error.message || 'Failed to change PlayerAI mode', { type: 'error' })

    } finally {

      setIsUpdatingPlayerAI(false)

    }

  }



  const formatDate = (timestamp: number) => {

    try {

      return new Date(timestamp).toLocaleString()

    } catch {

      return 'Unknown date'

    }

  }



  const getCategoryColor = (category: string) => {

    const colors: Record<string, string> = {

      'Token': 'bg-yellow-500/20 text-yellow-400',

      'Game Mechanics': 'bg-blue-500/20 text-blue-400',

      'Blockchain': 'bg-purple-500/20 text-purple-400',

      'UI/UX': 'bg-pink-500/20 text-pink-400',

      'Buffs & NFTs': 'bg-green-500/20 text-green-400',

      'Battle System': 'bg-red-500/20 text-red-400',

      'Other': 'bg-gray-500/20 text-gray-400'

    }

    return colors[category] || colors['Other']

  }



  const getLogLevelStyles = (level: string) => {

    const normalized = (level || 'info').toLowerCase()

    if (normalized === 'error') {

      return 'bg-red-500/20 text-red-300 border border-red-500/40'

    }

    if (normalized === 'warn' || normalized === 'warning') {

      return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'

    }

    if (normalized === 'debug') {

      return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'

    }

    return 'bg-blue-500/20 text-blue-300 border border-blue-500/40'

  }



  // AGC: Login Screen

  if (!isAuthenticated) {

    return (

      <div className="bg-slate-800 rounded-xl p-8 max-w-md w-full mx-auto">

        <div className="flex items-center justify-between mb-6">

          <div className="flex items-center gap-3">

            <Shield className="w-6 h-6 text-blue-400" />

            <h2 className="text-2xl font-bold text-white">Admin Login</h2>

          </div>

          <button

            onClick={onClose}

            className="text-gray-400 hover:text-white transition-colors"

            aria-label="Close"

          >

            <X className="w-6 h-6" />

          </button>

        </div>



        {loginError && (

          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-sm flex items-center gap-2">

            <AlertCircle className="w-4 h-4" />

            {loginError}

          </div>

        )}



        <form onSubmit={handleLogin} className="space-y-4">

          <div>

            <label htmlFor="username" className="block text-sm font-semibold text-gray-300 mb-2">

              Username

            </label>

            <input

              id="username"

              type="text"

              value={username}

              onChange={(e) => setUsername(e.target.value)}

              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"

              placeholder="Enter username"

              required

            />

          </div>



          <div>

            <label htmlFor="password" className="block text-sm font-semibold text-gray-300 mb-2">

              Password

            </label>

            <input

              id="password"

              type="password"

              value={password}

              onChange={(e) => setPassword(e.target.value)}

              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"

              placeholder="Enter password"

              required

            />

          </div>



          <button

            type="submit"

            className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg transition-all flex items-center justify-center gap-2"

          >

            <Shield className="w-4 h-4" />

            Login

          </button>

        </form>

      </div>

    )

  }



  const queueDetails = ['normal', 'expert', 'insane'].map((difficulty) => {

    const entries = playerAIStatus?.queue?.[difficulty] || []

    const label = difficulty === 'normal' ? 'Normal' : difficulty === 'expert' ? 'Expert' : 'Insane'



    return {

      difficulty,

      label,

      total: entries.length

    }

  })



  // AGC: Admin Dashboard

  if (selectedSuggestion) {

    return (

      <AdminSuggestionDetail

        suggestion={selectedSuggestion}

        authHeader={authHeader!}

        onBack={() => setSelectedSuggestion(null)}

        onClose={onClose}

        onRefresh={() => fetchSuggestions(authHeader!)}

      />

    )

  }



  const formatBalance = (balance: string, decimals: number = 18) => {

    try {

      return ethers.formatUnits(balance, decimals)

    } catch {

      return '0'

    }

  }



  const formatSignedOgbChange = (rawValue?: string) => {

    if (rawValue === undefined || rawValue === null) {

      return { display: '0', isPositive: true }

    }



    try {

      const value = BigInt(rawValue)

      const isPositive = value >= 0n

      const formatted = ethers.formatUnits(isPositive ? value : -value, 18)

      return { display: formatted, isPositive }

    } catch {

      return { display: '0', isPositive: true }

    }

  }



  const getBuffQuantity = (wallet: PlayerAIWallet, buffId: number): number => {

    const buffEntry = wallet.status?.buffs.find((buff) => Number(buff.id) === buffId)

    if (!buffEntry) {

      return 0

    }



    try {

      return Number(BigInt(buffEntry.balance))

    } catch {

      const numericValue = Number(buffEntry.balance)

      return Number.isFinite(numericValue) ? numericValue : 0

    }

  }



  const getPlayerRecord = (wallet: PlayerAIWallet) => {

    const stats = wallet.status?.playerData

    if (!stats) {

      return { total: 0, wins: 0, losses: 0, draws: 0 }

    }



    const total = stats.totalBattles ?? 0

    const wins = stats.wins ?? 0

    const losses = stats.losses ?? 0

    const draws = stats.draws ?? Math.max(total - wins - losses, 0)



    return { total, wins, losses, draws }

  }



  return (
    <div className="relative w-full h-full text-white bg-slate-950">
      {/* AGC: Dark overlay when mobile nav is open */}
      {isNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm md:hidden"
          onClick={() => setIsNavOpen(false)}
        />
      )}

      <div className="relative z-50 bg-slate-950 h-full overflow-hidden flex flex-col">
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* AGC: Hero header with safe-area padding */}
          <div
            className="flex flex-col gap-4 border-b border-white/10 bg-slate-950/80 px-5 py-4 md:flex-row md:items-center md:justify-between"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
          >
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-blue-400 drop-shadow-glow" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.35em] text-blue-200/70">Origami Command</p>
                <h2 className="text-2xl font-bold text-white">Admin Deck</h2>
                {backendStatus && (
                  <p className="text-xs text-gray-400 mt-1">
                    Backend {backendStatus.version || 'unknown'} • {backendStatus.network} • Uptime {backendStatus.uptime}
                  </p>
                )}
                {backendStatusError && (
                  <p className="text-xs text-red-300 mt-1">
                    {backendStatusError}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                onClick={() => setIsNavOpen(true)}
                className="md:hidden flex items-center gap-2 rounded-2xl border border-white/10 bg-slate-900/60 px-3 py-2 text-sm font-semibold text-gray-200"
              >
                <Menu className="w-4 h-4" />
                Menu
              </button>

              <button
                onClick={handleRestartBackend}
                disabled={isRestarting || !authHeader}
                className="px-4 py-2 rounded-2xl bg-orange-500/90 hover:bg-orange-400 text-sm font-semibold flex items-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                title="Restart backend server"
              >
                <Server className="w-4 h-4" />
                {isRestarting ? 'Restarting...' : 'Restart Backend'}
              </button>

              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-2xl border border-white/10 bg-slate-900/60 text-sm font-semibold text-white/80 hover:text-white hover:bg-slate-900/80 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>

              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* AGC: Left drawer navigation */}
            <aside
              className={`fixed inset-y-0 left-0 z-50 w-72 transform border-r border-white/10 bg-slate-950/95 shadow-2xl transition-transform duration-300 md:static md:block md:translate-x-0 md:flex-shrink-0 ${
                isNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
              }`}
            >
              <div className="px-5 py-6 space-y-5 overflow-y-auto h-full">
                <div className="flex items-center justify-between md:hidden">
                  <span className="text-sm font-semibold text-gray-200">Command Menu</span>
                  <button
                    onClick={() => setIsNavOpen(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                    aria-label="Close navigation"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <nav className="space-y-3">
                  {navSections.map((section) => {
                    const Icon = section.icon
                    const hasChildren = Boolean(section.children?.length)
                    const isSectionActive = activeTab === section.tab
                    const isExpanded = expandedSection === section.key

                    return (
                      <div
                        key={section.key}
                        className={`rounded-2xl border px-4 py-3 transition-colors ${
                          isSectionActive ? 'border-blue-500/60 bg-blue-500/10' : 'border-white/5 bg-slate-900/40'
                        }`}
                      >
                        <button
                          onClick={() => handleSectionToggle(section)}
                          className="flex w-full items-center justify-between text-left"
                        >
                          <div className="flex items-center gap-3">
                            <div className="rounded-xl bg-slate-900/60 p-2 text-blue-300">
                              <Icon className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-white">{section.label}</p>
                              <p className="text-[11px] text-gray-400">{section.description}</p>
                            </div>
                          </div>
                          {hasChildren ? (
                            isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
                          ) : (
                            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500">Go</span>
                          )}
                        </button>

                        {hasChildren && isExpanded && (
                          <div className="mt-3 space-y-2">
                            {section.children!.map((child) => {
                              const isChildActive =
                                activeTab === child.tab &&
                                ((child.playerAISubTab && playerAISubTab === child.playerAISubTab) ||
                                  (child.stakingSubTab && stakingSubTab === child.stakingSubTab) ||
                                  (child.tourneySubTab && tourneySubTab === child.tourneySubTab))

                              return (
                                <button
                                  key={child.key}
                                  onClick={() =>
                                    handleNavSelection(child.tab, {
                                      playerAISubTab: child.playerAISubTab,
                                      stakingSubTab: child.stakingSubTab,
                                      tourneySubTab: child.tourneySubTab,
                                    })
                                  }
                                  className={`w-full rounded-xl border px-3 py-2 text-left transition-colors ${
                                    isChildActive
                                      ? 'border-blue-500/70 bg-blue-500/20 text-white'
                                      : 'border-white/5 bg-transparent text-gray-300 hover:text-white'
                                  }`}
                                >
                                  <p className="text-sm font-semibold">{child.label}</p>
                                  {child.description && <p className="text-[11px] text-gray-400">{child.description}</p>}
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </nav>
              </div>
            </aside>

            {/* AGC: Content column */}
            <div className="flex-1 overflow-hidden bg-slate-900/30">
              <div className="h-full overflow-y-auto px-4 py-5 md:px-6">
                {/* AGC: Tab Content */}

        {activeTab === 'playerai' && (

          <div>

            {/* AGC: PlayerAI view indicator */}

            <div className="mb-4 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.35em] text-gray-400">

              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-900/60 px-3 py-1 text-xs font-semibold text-white">

                {playerAISubTabLabels[playerAISubTab]}

                {playerAISubTab === 'errors' && errorLogs.length > 0 && (

                  <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">

                    {errorLogs.length}

                  </span>

                )}

              </span>

              <span className="text-gray-500">

                Use menu to switch

              </span>

            </div>



            {playerAISubTab === 'controls' && (

              <>

                <div className="bg-slate-700 border border-slate-600 rounded-lg p-4">

                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

                  <div>

                    <h3 className="text-lg font-semibold text-white">PlayerAI Controls</h3>

                    <p className="text-sm text-gray-400">Configure automated opponents to keep matchmaking active.</p>

                    {playerAIStatus === null && (

                      <p className="text-xs text-gray-500 mt-1">Loading PlayerAI status...</p>

                    )}

                  </div>

                  <div className="flex items-center gap-3">

                    <button

                      onClick={handleTogglePlayerAI}

                      disabled={!authHeader || playerAIStatus === null || isUpdatingPlayerAI}

                      className={`px-4 py-2 rounded-lg transition-colors text-white ${

                        playerAIStatus?.settings.enabled

                          ? 'bg-red-600 hover:bg-red-500'

                          : 'bg-green-600 hover:bg-green-500'

                      } disabled:opacity-50 disabled:cursor-not-allowed`}

                    >

                      {isUpdatingPlayerAI ? 'Updating...' : playerAIStatus?.settings.enabled ? 'Disable PlayerAI' : 'Enable PlayerAI'}

                    </button>

                  </div>

                </div>



                <div className="mt-4 flex flex-wrap gap-2">

                  {(['instantly', 'delay', 'creator'] as PlayerAISettings['mode'][]).map((modeOption) => {

                    const isActive = playerAIStatus?.settings.mode === modeOption

                    const label = modeOption === 'instantly' ? 'Instantly (20s)' : modeOption === 'delay' ? 'Delay (3m)' : 'Creator Mode'



                    return (

                      <button

                        key={modeOption}

                        onClick={() => handleModeChange(modeOption)}

                        disabled={!authHeader || playerAIStatus?.settings.mode === modeOption || isUpdatingPlayerAI}

                        className={`px-3 py-1.5 rounded-lg text-sm transition-all border ${

                          isActive

                            ? 'bg-blue-600 border-blue-400 text-white'

                            : 'bg-slate-800 border-slate-600 text-gray-300 hover:border-blue-500'

                        } disabled:opacity-50 disabled:cursor-not-allowed`}

                      >

                        {label}

                      </button>

                    )

                  })}

                </div>



                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">

                  <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">

                    <p className="text-gray-400">Wallets Configured</p>

                    <p className="text-white text-lg font-semibold">{playerAIStatus?.totals.totalWallets ?? 0}</p>

                    <p className="text-xs text-gray-500">{playerAIStatus?.totals.fundedWallets ?? 0} funded • {playerAIStatus?.totals.engagedWallets ?? 0} engaged</p>

                  </div>

                  {queueDetails.map((detail) => (

                    <div key={detail.difficulty} className="bg-slate-800 rounded-lg p-3 border border-slate-700">

                      <p className="text-gray-400">{detail.label} Queue</p>

                      <p className="text-white text-lg font-semibold">{detail.total}</p>

                      <p className="text-xs text-gray-500">Players waiting</p>

                    </div>

                  ))}

                </div>

                </div>

                <div className="mt-6 bg-slate-700 border border-slate-600 rounded-lg p-4">

                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

                    <div>

                      <h3 className="text-lg font-semibold text-white">QA Debug Toggles</h3>

                      <p className="text-sm text-gray-400">
                        Enable or disable the floating debug helpers globally (Base App + desktop) through the backend settings API.
                      </p>

                    </div>

                    <p className="text-xs text-gray-500">
                      {isDebugSettingsLoading
                        ? 'Syncing global QA toggles from backend…'
                        : `Last updated: ${
                            debugSettingsLastUpdatedAt
                              ? new Date(debugSettingsLastUpdatedAt).toLocaleString()
                              : 'Just now'
                          }`}
                    </p>

                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">

                    {debugToggleOptions.map(({ key, label, description, icon: Icon }) => {

                      const isEnabled = debugSettings[key]

                      return (

                        <div key={key} className="bg-slate-800 border border-slate-700 rounded-lg p-4 flex flex-col justify-between">

                          <div>

                            <div className="flex items-center gap-2">

                              <Icon className="w-4 h-4 text-gray-300" />

                              <span className="text-white font-semibold">{label}</span>

                              <span

                                className={`ml-auto px-2 py-0.5 text-[11px] rounded-full ${isEnabled ? 'bg-emerald-600 text-emerald-50' : 'bg-slate-900 text-gray-400'}`}

                              >

                                {isEnabled ? 'ENABLED' : 'DISABLED'}

                              </span>

                            </div>

                            <p className="text-sm text-gray-400 mt-2">{description}</p>

                          </div>

                          <button
                            onClick={() => handleDebugSettingToggle(key)}
                            disabled={isDebugSettingsLoading}
                            className={`mt-4 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                              isEnabled ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-emerald-600 text-white hover:bg-emerald-500'
                            } ${isDebugSettingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {isDebugSettingsLoading ? 'Syncing…' : isEnabled ? 'Disable' : 'Enable'}
                          </button>

                        </div>

                      )

                    })}

                  </div>

                </div>

              </>

            )}



            {playerAISubTab === 'wallets' && (

              <div>

                <h3 className="text-lg font-semibold text-white mb-4">Configured Wallets</h3>

                {isLoadingWallets ? (

                  <div className="flex items-center justify-center py-12">

                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>

                    <span className="ml-3 text-gray-400">Loading wallets...</span>

                  </div>

                ) : wallets.length === 0 ? (

                  <div className="text-center py-12 text-gray-400">

                    <Wallet className="w-16 h-16 mx-auto mb-4 opacity-50" />

                    <p className="text-lg">No wallets configured</p>

                  </div>

                ) : (

                  <div className="space-y-3">

                    {wallets.map((wallet) => {

                      const isActive = Boolean(wallet.status?.meetsEthRequirement && wallet.status?.meetsOgbRequirement)

                      const level = wallet.status?.playerData?.level ?? null

                      const record = getPlayerRecord(wallet)

                      const ogbChange = formatSignedOgbChange(wallet.status?.ogbProfit)

                      const baselineOgb = wallet.status?.ogbBaseline ? formatBalance(wallet.status.ogbBaseline, 18) : '1000'



                      return (

                        <div key={wallet.index} className="bg-slate-700 border border-slate-600 rounded-lg p-4">

                          <div className="flex items-start justify-between mb-3">

                            <div>

                              <div className="flex items-center gap-2 mb-1">

                                <h4 className="text-lg font-semibold text-white">Wallet {wallet.index}</h4>

                                {wallet.status && (

                                  <span

                                    className={`px-2 py-1 text-xs font-semibold rounded ${isActive ? 'bg-green-600 text-green-100' : 'bg-red-600 text-red-100'}`}

                                  >

                                    {isActive ? 'ACTIVE' : 'INACTIVE'}

                                  </span>

                                )}

                                {wallet.isEngaged && (

                                  <span className="px-2 py-1 bg-yellow-600 text-yellow-100 text-xs rounded">Engaged</span>

                                )}

                                {wallet.isSharedWallet && (

                                  <span className="px-2 py-1 bg-purple-600 text-purple-100 text-xs rounded" title="Shares private key with another wallet">

                                    SHARED KEY

                                  </span>

                                )}

                              </div>

                              <p className="text-sm text-gray-400 font-mono">{wallet.address}</p>

                              {wallet.addressMismatch && wallet.configuredAddress && (
                                <p className="text-xs text-amber-400 mt-1">
                                  ⚠️ Configured address {wallet.configuredAddress} differs from private key address {wallet.address}. Using {wallet.address} for transactions.
                                </p>
                              )}

                              {wallet.isSharedWallet && (
                                <p className="text-xs text-purple-300 mt-1">
                                  ℹ️ This wallet shares the same private key as another wallet. Both will have the same address, balance, and signing capability.
                                </p>
                              )}

                              <p className="text-xs text-gray-500 mt-1">

                                Level {level ?? '-'} • Used {wallet.usageCount} {wallet.usageCount === 1 ? 'match' : 'matches'}

                              </p>

                            </div>

                            {wallet.status?.checkedAt && (

                              <div className="text-xs text-gray-500 text-right">

                                <p>Last Sync</p>

                                <p className="text-gray-300 font-mono">

                                  {new Date(wallet.status.checkedAt).toLocaleTimeString()}

                                </p>

                              </div>

                            )}

                          </div>



                          {wallet.status ? (

                            <>

                              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">

                                <div className="bg-slate-800 rounded p-3">

                                  <p className="text-gray-400 text-xs uppercase tracking-wide">ETH</p>

                                  <p className={`text-white font-semibold mt-1 ${wallet.status.meetsEthRequirement ? 'text-emerald-300' : 'text-red-300'}`}>

                                    {formatBalance(wallet.status.ethBalance, 18)}

                                  </p>

                                </div>

                                <div className="bg-slate-800 rounded p-3">

                                  <p className="text-gray-400 text-xs uppercase tracking-wide">OGB</p>

                                  <p className={`text-white font-semibold mt-1 ${wallet.status.meetsOgbRequirement ? 'text-emerald-300' : 'text-red-300'}`}>

                                    {formatBalance(wallet.status.ogbBalance, 18)}

                                  </p>

                                  <p className={`text-xs mt-2 ${ogbChange.isPositive ? 'text-emerald-300' : 'text-red-300'}`}>

                                    {ogbChange.isPositive ? 'PROFIT +' : 'LOSS -'}{ogbChange.display} OGB

                                  </p>

                                  <p className="text-[10px] text-gray-500 mt-1">Baseline: {baselineOgb} OGB</p>

                                </div>

                                <div className="bg-slate-800 rounded p-3">

                                  <p className="text-gray-400 text-xs uppercase tracking-wide">USDC</p>

                                  <p className={`text-white font-semibold mt-1 ${wallet.status.meetsUsdcRequirement ? 'text-emerald-300' : 'text-red-300'}`}>

                                    {formatBalance(wallet.status.usdcBalance, 6)}

                                  </p>

                                </div>

                                <div className="bg-slate-800 rounded p-3">

                                  <p className="text-gray-400 text-xs uppercase tracking-wide">Buff Inventory</p>

                                  <div className="mt-2 grid grid-cols-2 gap-2">

                                    {Object.entries(ADMIN_BUFF_TYPES).map(([buffId, info]) => {

                                      const quantity = getBuffQuantity(wallet, Number(buffId))

                                      return (

                                        <div key={buffId} className="bg-slate-900 rounded px-2 py-1 flex items-center justify-between text-xs">

                                          <span className="flex items-center gap-1 text-gray-300">

                                            {info.emoji} {info.label}

                                          </span>

                                          <span className="text-white font-semibold">{quantity}</span>

                                        </div>

                                      )

                                    })}

                                  </div>

                                </div>

                              </div>



                              <div className="mt-3 bg-slate-800 rounded p-3 text-sm">

                                <p className="text-gray-400 text-xs uppercase tracking-wide">Battle Record</p>

                                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-gray-300">

                                  <div>

                                    <p className="text-xs text-gray-500">Games</p>

                                    <p className="text-white font-semibold">{record.total}</p>

                                  </div>

                                  <div>

                                    <p className="text-xs text-gray-500">Won</p>

                                    <p className="text-emerald-300 font-semibold">{record.wins}</p>

                                  </div>

                                  <div>

                                    <p className="text-xs text-gray-500">Lost</p>

                                    <p className="text-red-300 font-semibold">{record.losses}</p>

                                  </div>

                                  <div>

                                    <p className="text-xs text-gray-500">Draws</p>

                                    <p className="text-sky-300 font-semibold">{record.draws}</p>

                                  </div>

                                </div>

                              </div>

                            </>

                          ) : (

                            <p className="text-sm text-gray-500">Status not available</p>

                          )}

                        </div>

                      )

                    })}

                  </div>

                )}

              </div>

            )}



            {playerAISubTab === 'errors' && (

              <div>

                <div className="flex items-center justify-between mb-4">

                  <h3 className="text-lg font-semibold text-white">Error Logs</h3>

                  {errorLogs.length > 0 && (

                    <button

                      onClick={handleClearErrors}

                      className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm rounded-lg transition-colors"

                    >

                      Clear All

                    </button>

                  )}

                </div>

                {isLoadingErrors ? (

                  <div className="flex items-center justify-center py-12">

                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>

                    <span className="ml-3 text-gray-400">Loading errors...</span>

                  </div>

                ) : errorLogs.length === 0 ? (

                  <div className="text-center py-12 text-gray-400">

                    <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />

                    <p className="text-lg">No errors logged</p>

                    <p className="text-sm mt-2">PlayerAI errors will appear here</p>

                  </div>

                ) : (

                  <div className="space-y-3 max-h-96 overflow-y-auto">

                    {errorLogs.map((error) => (

                      <div key={error.id} className="bg-slate-700 border border-red-600/50 rounded-lg p-4">

                        <div className="flex items-start justify-between mb-2">

                          <div className="flex items-center gap-2">

                            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />

                            <span className="px-2 py-1 bg-red-600/20 text-red-400 text-xs font-semibold rounded">

                              {error.type}

                            </span>

                          </div>

                          <span className="text-xs text-gray-400">

                            {new Date(error.timestamp).toLocaleString()}

                          </span>

                        </div>

                        <p className="text-red-200 text-sm mb-2 font-medium">{error.message}</p>

                        {error.context && Object.keys(error.context).length > 0 && (

                          <details className="mt-2">

                            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">

                              View Details

                            </summary>

                            <div className="mt-2 p-2 bg-slate-800 rounded text-xs text-gray-300 font-mono overflow-x-auto">

                              <pre>{JSON.stringify(error.context, null, 2)}</pre>

                            </div>

                          </details>

                        )}

                      </div>

                    ))}

                  </div>

                )}

              </div>

            )}

          </div>

        )}

        {/* AGC: Staking Tab */}
        {activeTab === 'staking' && (
          <div>
            <div className="mb-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.35em] text-gray-400">
              <span className="rounded-full border border-white/10 bg-slate-900/60 px-3 py-1 text-xs font-semibold text-white">
                {stakingSubTabLabels[stakingSubTab]}
              </span>
              <span className="text-gray-500">Use menu to switch</span>
            </div>

            {/* AGC: Anticipate Payout Subtab */}
            {stakingSubTab === 'anticipate' && (
              <AnticipatePayout />
            )}

            {/* AGC: Admin Controls Subtab */}
            {stakingSubTab === 'controls' && (
              <StakingAdminControls />
            )}
          </div>
        )}

        {activeTab === 'backendLogs' && (

          <div>

            <div className="flex items-center justify-between mb-4">

              <div>

                <h3 className="text-lg font-semibold text-white">Backend Logs</h3>

                <p className="text-sm text-gray-400">Newest entries first. Data refreshes when you open this tab or click refresh.</p>

              </div>

              <button

                onClick={handleRefreshBackendLogs}

                disabled={isLoadingBackendLogs}

                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${

                  isLoadingBackendLogs

                    ? 'bg-slate-700 text-gray-400 border-slate-600 cursor-not-allowed'

                    : 'bg-slate-700 hover:bg-slate-600 text-white border-slate-500'

                }`}

              >

                {isLoadingBackendLogs ? 'Refreshing...' : 'Refresh'}

              </button>

            </div>



            {backendLogsError && (

              <div className="mb-4 p-3 bg-red-900/40 border border-red-500 rounded-lg text-red-200 text-sm flex items-center gap-2">

                <AlertCircle className="w-4 h-4 flex-shrink-0" />

                {backendLogsError}

              </div>

            )}



            {isLoadingBackendLogs && backendLogs.length === 0 ? (

              <div className="flex items-center justify-center py-12">

                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>

                <span className="ml-3 text-gray-400">Loading backend logs...</span>

              </div>

            ) : backendLogs.length === 0 ? (

              <div className="text-center py-12 text-gray-400">

                <Server className="w-16 h-16 mx-auto mb-4 opacity-50" />

                <p className="text-lg">No backend logs captured yet</p>

                <p className="text-sm mt-2">New entries will appear here in real-time.</p>

              </div>

            ) : (

              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">

                {backendLogs.map((log) => (

                  <div

                    key={`${log.id}-${log.timestamp}`}

                    className="bg-slate-800 border border-slate-700 rounded-lg p-4"

                  >

                    <div className="flex items-start justify-between gap-4 mb-2">

                      <div className="flex items-center gap-2">

                        <span className={`px-2 py-1 text-xs font-semibold rounded ${getLogLevelStyles(log.level)}`}>

                          {log.level?.toUpperCase?.() || 'INFO'}

                        </span>

                        {log.source && (

                          <span className="text-xs text-blue-300 bg-blue-500/10 border border-blue-500/30 px-2 py-1 rounded">

                            {log.source}

                          </span>

                        )}

                      </div>

                      <div className="text-xs text-gray-400 text-right">

                        <p>{formatDate(log.timestamp)}</p>

                        {log.ip && <p>IP: {log.ip}</p>}

                      </div>

                    </div>

                    <p className="text-sm text-gray-100 whitespace-pre-wrap break-words">{log.message}</p>

                    {log.data && Object.keys(log.data).length > 0 && (

                      <details className="mt-3">

                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-200">

                          View payload

                        </summary>

                        <div className="mt-2 bg-slate-900 border border-slate-700 rounded p-3 text-xs text-gray-300 font-mono overflow-x-auto">

                          <pre>{JSON.stringify(log.data, null, 2)}</pre>

                        </div>

                      </details>

                    )}

                  </div>

                ))}

              </div>

            )}

          </div>

        )}

        {activeTab === 'tourney' && (

          <div className="space-y-6">

            <div className="mb-2 flex items-center gap-3 text-[11px] uppercase tracking-[0.35em] text-gray-400">
              <span className="rounded-full border border-white/10 bg-slate-900/60 px-3 py-1 text-xs font-semibold text-white">
                {tourneySubTabLabels[tourneySubTab]}
              </span>
              <span className="text-gray-500">Use menu to switch</span>
            </div>

            {/* AGC: AI Settings Subtab */}
            {tourneySubTab === 'settings' && (
            <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">

              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">

                <Trophy className="w-5 h-5" />

                TournamentAI Settings

              </h2>

              {tournamentAIStatus === null ? (

                <div className="flex items-center justify-center py-12">

                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>

                  <span className="ml-3 text-gray-400">Loading TournamentAI status...</span>

                </div>

              ) : (

                <div className="space-y-4">

                  {/* AGC: Auto-Create Tournaments Toggle */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white">Auto-Create Tournaments</h3>
                        <p className="text-sm text-gray-400">Automatically create new tournaments when conditions are met</p>
                        {tournamentTimers?.autoCreateTournaments === false && (
                          <p className="text-xs text-yellow-400 mt-1">Auto-create is disabled - use "Start Tournament" button to create manually</p>
                        )}
                      </div>
                      <button
                        onClick={handleToggleAutoCreateTournaments}
                        disabled={!authHeader || isUpdatingTimers || tournamentTimers === null}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          tournamentTimers?.autoCreateTournaments !== false ? 'bg-blue-600' : 'bg-gray-600'
                        } ${isUpdatingTimers || tournamentTimers === null ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            tournamentTimers?.autoCreateTournaments !== false ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {tournamentTimers?.autoCreateTournaments === false && (
                      <button
                        onClick={handleStartTournament}
                        disabled={!authHeader || isStartingTournament}
                        className={`w-full mt-3 px-4 py-2 rounded-lg font-medium transition-colors ${
                          isStartingTournament
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {isStartingTournament ? 'Starting Tournament...' : 'Start Tournament'}
                      </button>
                    )}
                  </div>

                  {/* AGC: Enabled Toggle (now also controls auto-join) */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">

                    <div className="flex items-center justify-between mb-3">

                      <div>

                        <h3 className="text-lg font-semibold text-white">TournamentAI Enabled</h3>

                        <p className="text-sm text-gray-400">Enable/disable TournamentAI system and auto-join</p>
                        {tournamentAIStatus?.settings?.enabled && (
                          <p className="text-xs text-blue-400 mt-1">Auto-join is automatically enabled when TournamentAI is on</p>
                        )}

                      </div>

                      <button

                        type="button"

                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleToggleTournamentAIEnabled()
                        }}

                        disabled={!authHeader || isUpdatingTournamentAI || !tournamentAIStatus}

                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors z-10 ${

                          tournamentAIStatus?.settings?.enabled ? 'bg-blue-600' : 'bg-gray-600'

                        } ${isUpdatingTournamentAI || !tournamentAIStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}

                      >

                        <span

                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${

                            tournamentAIStatus?.settings?.enabled ? 'translate-x-6' : 'translate-x-1'

                          }`}

                        />

                      </button>

                    </div>

                  </div>

                  {/* AGC: Max AI Per Tournament */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">

                    <h3 className="text-lg font-semibold text-white mb-3">Max AI Per Tournament</h3>

                    <p className="text-sm text-gray-400 mb-3">How many TournamentAI players should join each tournament</p>

                    <div className="flex items-center gap-3">

                      <select

                        value={tournamentAIStatus.settings.maxPerTournament || 1}

                        onChange={(e) => {

                          const value = parseInt(e.target.value, 10)

                          if (!isNaN(value) && value >= 1 && value <= 5) {

                            handleUpdateMaxPerTournament(value)

                          }

                        }}

                        disabled={!authHeader || isUpdatingTournamentAI || !tournamentAIStatus.settings.enabled}

                        className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"

                      >

                        <option value={1}>1</option>

                        <option value={2}>2</option>

                        <option value={3}>3</option>

                        <option value={4}>4</option>

                        <option value={5}>5</option>

                      </select>

                      <span className="text-sm text-gray-400">

                        {tournamentAIStatus.settings.maxPerTournament === 1

                          ? '1 AI will join per tournament'

                          : `${tournamentAIStatus.settings.maxPerTournament} AIs will join per tournament`}

                      </span>

                    </div>

                  </div>

                  {/* AGC: Allowed Wallets Selector */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">

                    <h3 className="text-lg font-semibold text-white mb-3">Allowed Wallets</h3>

                    <p className="text-sm text-gray-400 mb-3">Select which TournamentAI wallets can be used (multiple selection). Empty selection means all wallets are allowed.</p>

                    {tournamentAIStatus.wallets.length === 0 ? (

                      <p className="text-sm text-gray-500">No TournamentAI wallets configured</p>

                    ) : (

                      <div className="space-y-2 max-h-64 overflow-y-auto">

                        {tournamentAIStatus.wallets.map((wallet) => {

                          const isSelected = tournamentAIStatus.settings.allowedWallets?.includes(wallet.address.toLowerCase()) ?? true

                          return (

                            <label

                              key={wallet.address}

                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${

                                isSelected

                                  ? 'bg-blue-900/30 border-blue-500/50'

                                  : 'bg-slate-800 border-slate-600 hover:bg-slate-700'

                              } ${!authHeader || isUpdatingTournamentAI || !tournamentAIStatus.settings.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}

                            >

                              <input

                                type="checkbox"

                                checked={isSelected}

                                onChange={(e) => {

                                  if (!authHeader || isUpdatingTournamentAI || !tournamentAIStatus.settings.enabled) return

                                  const currentAllowed = tournamentAIStatus.settings.allowedWallets || wallets.map(w => w.address.toLowerCase())

                                  const newAllowed = e.target.checked

                                    ? [...currentAllowed, wallet.address.toLowerCase()]

                                    : currentAllowed.filter(addr => addr !== wallet.address.toLowerCase())

                                  handleUpdateAllowedWallets(newAllowed)

                                }}

                                disabled={!authHeader || isUpdatingTournamentAI || !tournamentAIStatus.settings.enabled}

                                className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500 focus:ring-2"

                              />

                              <div className="flex-1 min-w-0">

                                <p className="text-sm font-mono text-white truncate">{wallet.address}</p>

                                <p className="text-xs text-gray-400">

                                  {wallet.status?.isFunded ? '✅ Funded' : '❌ Not Funded'} • ETH: {wallet.status?.meetsEthRequirement ? 'OK' : 'Low'} • OGB: {wallet.status?.meetsOgbRequirement ? 'OK' : 'Low'}

                                </p>

                              </div>

                            </label>

                          )

                        })}

                      </div>

                    )}

                  </div>

                  {/* AGC: Status Summary */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">

                    <h3 className="text-lg font-semibold text-white mb-3">Status Summary</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">

                      <div>

                        <p className="text-gray-400">Total Wallets</p>

                        <p className="text-white font-semibold text-lg">{tournamentAIStatus.totals.totalWallets}</p>

                      </div>

                      <div>

                        <p className="text-gray-400">Funded Wallets</p>

                        <p className="text-emerald-300 font-semibold text-lg">{tournamentAIStatus.totals.fundedWallets}</p>

                      </div>

                      <div>

                        <p className="text-gray-400">Engaged Wallets</p>

                        <p className="text-blue-300 font-semibold text-lg">{tournamentAIStatus.totals.engagedWallets}</p>

                      </div>

                    </div>

                  </div>

                  <div className="mt-4 p-3 bg-blue-900/30 border border-blue-500/50 rounded-lg">

                    <p className="text-blue-300 text-xs">

                      TournamentAI automatically: joins tournaments, pays entry fees, marks ready, starts matches, confirms battles, and plays through all rounds.

                    </p>

                  </div>

                </div>

              )}

            </div>
            )}

            {/* AGC: Timers Subtab */}
            {tourneySubTab === 'timers' && (
            <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Tournament Timers
                </h2>
                <button
                  onClick={handleResetTimers}
                  disabled={isUpdatingTimers || !authHeader}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-600 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center gap-2 text-sm"
                >
                  <RefreshCw className={`w-4 h-4 ${isUpdatingTimers ? 'animate-spin' : ''}`} />
                  Reset to Defaults
                </button>
              </div>

              {isLoadingTimers ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  <span className="ml-3 text-gray-400">Loading timer settings...</span>
                </div>
              ) : tournamentTimers === null ? (
                <div className="text-center py-12 text-gray-400">
                  <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Failed to load timer settings</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* AGC: Lobby Timer (Tournament Timer) */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white">Tournament Timer (Lobby)</h3>
                        <p className="text-sm text-gray-400">Time for players to join tournament before it auto-starts or cancels</p>
                      </div>
                      <span className="px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-lg">
                        {formatTimerMs(tournamentTimers.lobbyTimerMs)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="30000"
                        max="600000"
                        step="30000"
                        value={tournamentTimers.lobbyTimerMs}
                        onChange={(e) => handleUpdateTimer('lobbyTimerMs', Number(e.target.value))}
                        disabled={isUpdatingTimers}
                        className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                      <div className="flex gap-1">
                        {[60000, 120000, 180000, 300000].map((ms) => (
                          <button
                            key={ms}
                            onClick={() => handleUpdateTimer('lobbyTimerMs', ms)}
                            disabled={isUpdatingTimers}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              tournamentTimers.lobbyTimerMs === ms
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            }`}
                          >
                            {formatTimerMs(ms)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* AGC: Ready Timer (Click Ready Timer) */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white">Click Ready Timer</h3>
                        <p className="text-sm text-gray-400">Time for players to click "Ready" after countdown phase starts</p>
                      </div>
                      <span className="px-3 py-1 bg-green-600 text-white text-sm font-semibold rounded-lg">
                        {formatTimerMs(tournamentTimers.readyTimerMs)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="30000"
                        max="600000"
                        step="30000"
                        value={tournamentTimers.readyTimerMs}
                        onChange={(e) => handleUpdateTimer('readyTimerMs', Number(e.target.value))}
                        disabled={isUpdatingTimers}
                        className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-green-600"
                      />
                      <div className="flex gap-1">
                        {[60000, 120000, 180000, 300000].map((ms) => (
                          <button
                            key={ms}
                            onClick={() => handleUpdateTimer('readyTimerMs', ms)}
                            disabled={isUpdatingTimers}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              tournamentTimers.readyTimerMs === ms
                                ? 'bg-green-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            }`}
                          >
                            {formatTimerMs(ms)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* AGC: Play Match Timer */}
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-white">Play Match Timer</h3>
                        <p className="text-sm text-gray-400">Time for players to click Play Match, choose NFT, and confirm</p>
                        <p className="text-xs text-amber-400 mt-1">⚠️ If timeout: both players didn't act = both lose, one didn't = that one loses</p>
                      </div>
                      <span className="px-3 py-1 bg-amber-600 text-white text-sm font-semibold rounded-lg">
                        {formatTimerMs(tournamentTimers.playMatchTimerMs)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="30000"
                        max="600000"
                        step="30000"
                        value={tournamentTimers.playMatchTimerMs}
                        onChange={(e) => handleUpdateTimer('playMatchTimerMs', Number(e.target.value))}
                        disabled={isUpdatingTimers}
                        className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-amber-600"
                      />
                      <div className="flex gap-1">
                        {[60000, 120000, 180000, 300000].map((ms) => (
                          <button
                            key={ms}
                            onClick={() => handleUpdateTimer('playMatchTimerMs', ms)}
                            disabled={isUpdatingTimers}
                            className={`px-2 py-1 text-xs rounded transition-colors ${
                              tournamentTimers.playMatchTimerMs === ms
                                ? 'bg-amber-600 text-white'
                                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            }`}
                          >
                            {formatTimerMs(ms)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* AGC: Info Box */}
                  <div className="mt-4 p-3 bg-blue-900/30 border border-blue-500/50 rounded-lg">
                    <p className="text-blue-300 text-xs">
                      <strong>Note:</strong> Timer changes apply to new tournaments only. Existing tournaments use the timers set when they were created.
                      All timers must be between 30 seconds and 10 minutes.
                    </p>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* AGC: Ongoing Tournaments Subtab */}
            {tourneySubTab === 'ongoing' && (
            <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">

              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">

                <Trophy className="w-5 h-5" />

                Ongoing Tournaments

              </h2>

              {isLoadingOngoingTournaments ? (

                <div className="flex items-center justify-center py-12">

                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>

                  <span className="ml-3 text-gray-400">Loading tournaments...</span>

                </div>

              ) : ongoingTournaments.length === 0 ? (

                <div className="text-center py-12 text-gray-400">

                  <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />

                  <p className="text-lg">No ongoing tournaments</p>

                </div>

              ) : (

                <div className="space-y-4">

                  {ongoingTournaments.map((tournament) => (

                    <div key={tournament.id} className="bg-slate-900 rounded-lg p-4 border border-slate-700">

                      <div className="flex items-start justify-between">

                        <div className="flex-1">

                          <div className="flex items-center gap-2 mb-2">

                            <h3 className="text-lg font-semibold text-white">Tournament {tournament.id.slice(0, 10)}...</h3>

                            <span className={`px-2 py-1 text-xs font-semibold rounded ${

                              tournament.status === 'RUNNING' ? 'bg-green-600 text-green-100' :

                              tournament.status === 'COUNTDOWN' ? 'bg-yellow-600 text-yellow-100' :

                              'bg-blue-600 text-blue-100'

                            }`}>

                              {tournament.status}

                            </span>

                          </div>

                          <div className="text-sm text-gray-400 space-y-1">

                            <p>Participants: {tournament.participantCount}</p>

                            {tournament.status === 'RUNNING' && tournament.currentRound > 0 && (

                              <p>Current Round: {tournament.currentRound}</p>

                            )}

                            {tournament.startedAt && (

                              <p>Started: {new Date(tournament.startedAt).toLocaleString()}</p>

                            )}

                            {!tournament.startedAt && tournament.createdAt && (

                              <p>Created: {new Date(tournament.createdAt).toLocaleString()}</p>

                            )}

                          </div>

                        </div>

                        <button

                          onClick={() => handleCancelTournament(tournament.id)}

                          disabled={isCancellingTournament === tournament.id || !authHeader}

                          className="px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-slate-600 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"

                        >

                          {isCancellingTournament === tournament.id ? 'Cancelling...' : 'Cancel & Refund'}

                        </button>

                      </div>

                    </div>

                  ))}

                </div>

              )}

            </div>
            )}

          </div>

        )}

        {activeTab === 'msgbox' && (

          <div>

            <h3 className="text-lg font-semibold text-white mb-2">All Suggestions</h3>

            <p className="text-sm text-gray-400 mb-4">Click on a suggestion to view details and respond</p>

            {isLoading ? (

              <div className="flex items-center justify-center py-12">

                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>

                <span className="ml-3 text-gray-400">Loading suggestions...</span>

              </div>

            ) : suggestions.length === 0 ? (

              <div className="text-center py-12 text-gray-400">

                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />

                <p className="text-lg">No suggestions yet</p>

              </div>

            ) : (

              <div className="space-y-3">

                {suggestions.map((suggestion) => (

                  <button

                    key={suggestion.id}

                    onClick={() => setSelectedSuggestion(suggestion)}

                    className="w-full text-left p-4 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors border border-slate-600"

                  >

                    <div className="flex items-start justify-between gap-4">

                      <div className="flex-1 min-w-0">

                        <div className="flex items-center gap-2 mb-2">

                          <h3 className="text-lg font-semibold text-white truncate">{suggestion.title}</h3>

                          <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${getCategoryColor(suggestion.category)}`}>

                            {suggestion.category}

                          </span>

                        </div>

                        <p className="text-sm text-gray-300 line-clamp-2 mb-2">{suggestion.text}</p>

                        <div className="flex items-center gap-4 text-xs text-gray-400">

                          <div className="flex items-center gap-1">

                            <MessageSquare className="w-4 h-4" />

                            <span>{suggestion.commentCount || 0} comments</span>

                          </div>

                          <div>

                            <span>{formatDate(suggestion.createdAt)}</span>

                          </div>

                        </div>

                      </div>

                    </div>

                  </button>

                ))}

              </div>

            )}

          </div>

        )}

        {/* AGC: Airdrop Tab */}
        {activeTab === 'airdrop' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Airdrop Control Center</h3>
            <AirdropAdminControls />
          </div>
        )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AGC: Modal components */}
      {Modals}
    </div>

  )

}



/**

 * AGC: Admin Suggestion Detail Component

 * AGC: Shows suggestion details with admin response functionality

 */




export const AdminSuggestionDetail: React.FC<AdminSuggestionDetailProps> = ({
  suggestion: initialSuggestion,
  authHeader,
  onBack,
  onClose,
  onRefresh
}) => {

  const [suggestion, setSuggestion] = useState<Suggestion>(initialSuggestion)

  const [commentText, setCommentText] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const [error, setError] = useState<string | null>(null)



  useEffect(() => {

    fetchSuggestionDetails()

  }, [suggestion.id])



  const fetchSuggestionDetails = async () => {

    try {

      setIsLoading(true)

      const response = await fetch(buildBackendUrl(`api/suggestions/${suggestion.id}`), {

        headers: {

          'Authorization': authHeader

        }

      })



      if (!response.ok) {

        throw new Error('Failed to fetch suggestion details')

      }



      const data = await response.json()

      setSuggestion(data.suggestion)

    } catch (err: any) {

      setError(err.message || 'Failed to load suggestion details')

    } finally {

      setIsLoading(false)

    }

  }



  const handleAddAdminResponse = async (e: React.FormEvent) => {

    e.preventDefault()

    setError(null)



    if (!commentText.trim()) {

      return

    }



    if (commentText.length > 2000) {

      setError('Comment must be 2000 characters or less')

      return

    }



    setIsSubmitting(true)



    try {

      const response = await fetch(buildBackendUrl(`api/suggestions/${suggestion.id}/comments`), {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

          'Authorization': authHeader

        },

        body: JSON.stringify({ text: commentText.trim(), isAdmin: true })

      })



      if (!response.ok) {

        const data = await response.json()

        throw new Error(data.error || 'Failed to add admin response')

      }



      const data = await response.json()

      

      // AGC: Add admin response to the list immediately

      const newComment = {

        ...data.comment,

        createdAt: data.comment.createdAt || Date.now(),

        isAdmin: true

      }

      

      setSuggestion({

        ...suggestion,

        comments: [...(suggestion.comments || []), newComment],

        commentCount: (suggestion.commentCount || 0) + 1

      })



      setCommentText('')

      

      // AGC: Refresh the full suggestion to ensure we have the latest data

      await fetchSuggestionDetails()

      onRefresh()

    } catch (err: any) {

      setError(err.message || 'Failed to add admin response')

    } finally {

      setIsSubmitting(false)

    }

  }



  const formatDate = (timestamp: number) => {

    try {

      return new Date(timestamp).toLocaleString()

    } catch {

      return 'Unknown date'

    }

  }



  const getCategoryColor = (category: string) => {

    const colors: Record<string, string> = {

      'Token': 'bg-yellow-500/20 text-yellow-400',

      'Game Mechanics': 'bg-blue-500/20 text-blue-400',

      'Blockchain': 'bg-purple-500/20 text-purple-400',

      'UI/UX': 'bg-pink-500/20 text-pink-400',

      'Buffs & NFTs': 'bg-green-500/20 text-green-400',

      'Battle System': 'bg-red-500/20 text-red-400',

      'Other': 'bg-gray-500/20 text-gray-400'

    }

    return colors[category] || colors['Other']

  }



  return (

    <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full mx-auto max-h-[90vh] flex flex-col">

      <div className="flex items-center justify-between mb-6">

        <div className="flex items-center gap-3">

          <button

            onClick={onBack}

            className="text-gray-400 hover:text-white transition-colors"

            aria-label="Back to list"

          >

             Back

          </button>

          <h2 className="text-2xl font-bold text-white">Admin Response</h2>

        </div>

        <button

          onClick={onClose}

          className="text-gray-400 hover:text-white transition-colors"

          aria-label="Close"

        >

          <X className="w-6 h-6" />

        </button>

      </div>



      {isLoading ? (

        <div className="flex items-center justify-center py-12">

          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>

          <span className="ml-3 text-gray-400">Loading...</span>

        </div>

      ) : error ? (

        <div className="p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200 mb-4">

          {error}

        </div>

      ) : (

        <>

          {/* AGC: Suggestion Content */}

          <div className="flex-1 overflow-y-auto space-y-6 mb-6">

            <div className="bg-slate-700 rounded-lg p-6">

              <div className="flex items-center gap-3 mb-4">

                <h3 className="text-2xl font-bold text-white">{suggestion.title}</h3>

                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(suggestion.category)}`}>

                  {suggestion.category}

                </span>

              </div>

              <div className="text-sm text-gray-400 mb-4">

                Posted {formatDate(suggestion.createdAt)}

              </div>

              <div className="text-gray-200 whitespace-pre-wrap leading-relaxed">

                {suggestion.text}

              </div>

            </div>



            {/* AGC: Comments Section */}

            <div>

              <h4 className="text-xl font-bold text-white mb-4">

                Comments ({suggestion.comments?.length || 0})

              </h4>



              {suggestion.comments && suggestion.comments.length > 0 ? (

                <div className="space-y-4">

                  {suggestion.comments.map((comment) => (

                    <div

                      key={comment.id}

                      className={`rounded-lg p-4 ${

                        comment.isAdmin

                          ? 'bg-blue-900/30 border-2 border-blue-500/50'

                          : 'bg-slate-700'

                      }`}

                    >

                      <div className="flex items-center gap-2 mb-2">

                        {comment.isAdmin && (

                          <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded flex items-center gap-1">

                            <Shield className="w-3 h-3" />

                            ADMIN

                          </span>

                        )}

                      </div>

                      <div className="text-gray-200 whitespace-pre-wrap mb-2">{comment.text}</div>

                      <div className="text-xs text-gray-400">{formatDate(comment.createdAt)}</div>

                    </div>

                  ))}

                </div>

              ) : (

                <div className="text-center py-8 text-gray-400">

                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />

                  <p>No comments yet.</p>

                </div>

              )}

            </div>

          </div>



          {/* AGC: Admin Response Form */}

          <div className="border-t border-slate-700 pt-6 mt-6">

            <div className="mb-4 p-3 bg-blue-900/30 border border-blue-500/50 rounded-lg">

              <div className="flex items-center gap-2 text-blue-300 text-sm font-semibold">

                <Shield className="w-4 h-4" />

                <span>You are responding as an admin. Your response will be marked with an admin badge.</span>

              </div>

            </div>

            <form onSubmit={handleAddAdminResponse} className="space-y-3">

              {error && (

                <div className="p-3 bg-red-900/50 border border-red-500 rounded-lg text-red-200 text-sm">

                  {error}

                </div>

              )}

              <div>

                <label htmlFor="admin-comment" className="block text-sm font-semibold text-gray-300 mb-2">

                  Admin Response

                </label>

                <textarea

                  id="admin-comment"

                  value={commentText}

                  onChange={(e) => setCommentText(e.target.value)}

                  maxLength={2000}

                  rows={4}

                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"

                  placeholder="Type your admin response here..."

                  disabled={isSubmitting}

                />

                <div className="text-xs text-gray-400 mt-1">{commentText.length}/2000 characters</div>

              </div>

              <button

                type="submit"

                disabled={isSubmitting || !commentText.trim()}

                className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"

              >

                {isSubmitting ? (

                  <>

                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>

                    Submitting...

                  </>

                ) : (

                  <>

                    <Shield className="w-4 h-4" />

                    Post Admin Response

                  </>

                )}

              </button>

            </form>

          </div>

        </>

        )}

    </div>

  )

}



